import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Checklist as ChecklistIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon
} from "@mui/icons-material";

const TaskChecklist = () => {
  const theme = useTheme();

  const checklistFeatures = [
    {
      icon: <SettingsIcon sx={{ fontSize: 40 }} />,
      title: "Customizable Templates",
      description: "Create and save checklists for different property types"
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40 }} />,
      title: "Team Collaboration",
      description: "Share checklists across your cleaning team"
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      title: "Completion Analytics",
      description: "Track performance and identify common missed items"
    },
    {
      icon: <NotificationsIcon sx={{ fontSize: 40 }} />,
      title: "Smart Reminders",
      description: "Get notifications for time-sensitive tasks"
    }
  ];

  const sampleChecklist = {
    "Living Area": [
      "Dust all surfaces and furniture",
      "Vacuum carpets and rugs",
      "Clean windows and mirrors",
      "Empty trash bins",
      "Organize cushions and pillows"
    ],
    "Kitchen": [
      "Clean countertops and backsplash",
      "Sanitize sink and faucet",
      "Wipe down appliances",
      "Clean inside microwave",
      "Sweep and mop floor"
    ],
    "Bathroom": [
      "Clean and disinfect toilet",
      "Scrub shower and tub",
      "Clean vanity and mirror",
      "Restock toilet paper",
      "Empty trash and mop floor"
    ],
    "Bedroom": [
      "Change bed linens",
      "Dust all furniture",
      "Vacuum floor",
      "Organize closet area",
      "Check under bed"
    ]
  };

  const benefits = [
    "Ensure consistent cleaning standards across all properties",
    "Reduce missed items and callback requests",
    "Train new cleaners more effectively",
    "Provide clear expectations for every job",
    "Save time with pre-configured checklists"
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Chip 
          label="✅ Task Checklist" 
          sx={{ 
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2.5rem", md: "3rem" }
          }}
        >
          Task Checklist
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Never miss a detail with our comprehensive cleaning checklists. Customizable templates 
          ensure every property receives the same high standard of cleaning, every time.
        </Typography>
      </Box>

      {/* Features Grid */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        {checklistFeatures.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ textAlign: "center", height: "100%", background: theme.palette.background.paper }}>
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    color: theme.palette.primary.main,
                    mb: 2
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom fontWeight="600">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color={theme.palette.text.secondary}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Sample Checklist & Benefits */}
      <Grid container spacing={6} sx={{ mb: 8 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            Benefits of Standardized Checklists
          </Typography>
          
          <List sx={{ mb: 4 }}>
            {benefits.map((benefit, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckIcon sx={{ color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText 
                  primary={benefit}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChecklistIcon />
              Sample Checklist
            </Typography>
            
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {Object.entries(sampleChecklist).map(([room, tasks]) => (
                <Box key={room} sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="600" color={theme.palette.primary.main} sx={{ mb: 1 }}>
                    {room}
                  </Typography>
                  <Stack spacing={1}>
                    {tasks.map((task, taskIndex) => (
                      <FormControlLabel
                        key={taskIndex}
                        control={
                          <Checkbox 
                            size="small" 
                            sx={{ 
                              color: theme.palette.primary.main,
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {task}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Customization Section */}
      <Paper sx={{ p: 6, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`, borderRadius: 4 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              Fully Customizable
            </Typography>
            <Typography variant="body1" color={theme.palette.text.secondary} sx={{ mb: 3, lineHeight: 1.7 }}>
              Create checklists tailored to each property's specific needs. Add custom tasks, 
              set priority levels, and include special instructions for your cleaning team.
            </Typography>
            
            <Stack spacing={2}>
              {[
                "Create templates for different property types (studio, 1-bedroom, luxury, etc.)",
                "Add photo requirements for specific tasks",
                "Set time estimates for each cleaning item",
                "Include special instructions for unique property features",
                "Duplicate and modify existing checklists"
              ].map((feature, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <CheckIcon sx={{ color: theme.palette.primary.main, mt: 0.5 }} />
                  <Typography variant="body1">{feature}</Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: theme.palette.background.paper,
                borderRadius: 3,
                p: 4,
                textAlign: 'center'
              }}
            >
              <ChecklistIcon sx={{ fontSize: 80, color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight="600">
                Property Types Supported
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mt: 2 }}>
                {["Studio", "1-Bedroom", "2-Bedroom", "3+ Bedroom", "Luxury", "Vacation Home", "Commercial", "Office"].map((type, idx) => (
                  <Chip
                    key={idx}
                    label={type}
                    variant="outlined"
                    sx={{
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default TaskChecklist;